import {default as cx} from "classnames"
import {Dropdown} from "react-bootstrap"
import {useNavigate} from "react-router-dom"

import {EducatorOrientationFields} from "./EducatorOrientationFields"
import {ProgressRating} from "./ProgressRating"
import {scoreBadge, getInitials} from "../../utilities/general"

const orientation_status = {
    Onboarding: {bgColor: "badge-light-success", color: "text-success"},
    Active: {bgColor: "badge-light-primary", color: "text-primary"},
    Paused: {bgColor: "badge-light-dark", color: ""},
    Attritioned: {bgColor: "badge-light-warning", color: "text-warning"},
    Cancelled: {bgColor: "badge-light-danger", color: "text-danger"},
    Completed: {bgColor: "badge-light-success", color: "text-success"},
    Transfer: {bgColor: "badge-light-info", color: "text-info"},
}

export const EducatorOrientationSummary = ({orientation}) => {
    const navigate = useNavigate()
    const osrMax = scoreBadge(orientation.current_readiness_rating, 100)
    const assessmentBadge = scoreBadge(orientation.assessment_and_care_percent_score, 100)
    const clinicalBadge = scoreBadge(orientation.clinical_practice_percent_score, 100)
    const educationBadge = scoreBadge(orientation.education_communication_collaboration_percent_score, 100)
    const medicationBadge = scoreBadge(orientation.medication_management_percent_score, 100)
    const preventionBadge = scoreBadge(orientation.prevention_safety_quality_percent_score, 100)
    const professionalismBadge = scoreBadge(orientation.professionalism_percent_score, 100)
    const orientation_color_mapping = orientation_status[orientation.orientation_status]
    const futureStartDate = new Date() < new Date(orientation.start_date)
    // Fall back to the single latest week if the array isn't present (older API response).
    const allMissingWeeks =
        orientation.missing_progress_report_weeks && orientation.missing_progress_report_weeks.length
            ? orientation.missing_progress_report_weeks
            : !Number.isNaN(parseInt(orientation.latest_missing_progress_report_week))
            ? [parseInt(orientation.latest_missing_progress_report_week)]
            : []

    // Two highest week numbers, shown highest first
    const missingWeeks = [...allMissingWeeks]
        .map(Number)
        .filter((w) => Number.isFinite(w))
        .sort((a, b) => b - a)
        .slice(0, 2)

    const progressReportDue = () => {
        let isReportDue = true
        if (orientation.orientation_status === "Paused"){
            return false
        }
        else if (futureStartDate == true){
            return false
        }
        else if(missingWeeks.length > 0){
            return true
        }
    }

    const SubmissionStatus = ({label, isSubmitted}) => (
        <span className={cx("fw-semibold fs-7", {"text-danger": !isSubmitted})}>
            <i
                className={cx(
                    "fas me-1 fs-7",
                    isSubmitted ? "fa-circle-check text-success" : "fa-exclamation-triangle text-danger"
                )}
            ></i>
            {label}
        </span>
    )

    return (
        <div className="card mt-4 border border-gray-400">
            <div className="card-header flex-column flex-sm-row align-items-stretch align-items-sm-center px-4 py-4 gap-3 gap-sm-0">
                <div className="card-title text-dark fw-bold gap-4 flex-fill m-0">
                    <div className="d-flex flex-row align-items-start align-items-sm-center gap-2 flex-fill">
                        <div className="symbol symbol-30px d-none d-md-flex me-2 align-self-start">
                            <div className="symbol-label fs-5 fw-semibold bg-primary text-inverse-danger">
                                {getInitials(orientation.name) || "?"}
                            </div>
                        </div>
                        <div className="d-flex flex-column fs-5 flex-fill gap-2">
                            <div className="d-flex flex-row flex-wrap align-items-center gap-2">
                                <div className="educator-orientee-name-horiztonal-spacing fs-4">{orientation.name}</div>
                                {orientation.orientation_status && (
                                    <span
                                        className={`badge flex-center px-2 py-1 ${orientation_color_mapping.bgColor} ${orientation_color_mapping.color}`}
                                    >
                                        {futureStartDate ? "Waiting to Start" : orientation.orientation_status}
                                    </span>
                                )}
                            </div>
                            <div className="d-flex flex-row flex-wrap gap-2 gap-sm-3 align-items-center">
                                <span className="text-dark fw-bold fs-7">
                                    Week{" "}
                                    {orientation.current_week_of_hire === null
                                        ? "N/A"
                                        : orientation.current_week_of_hire}
                                </span>
                                {orientation.current_readiness_rating > 0 && (
                                    <span className="d-flex align-items-center gap-2 fs-7">
                                        <span className="text-dark fw-bold ">Readiness:</span>
                                        <span className={`badge badge-sm p-2 ${osrMax.background}`}>
                                            {`${osrMax.badge}: ${osrMax.score}%`}
                                        </span>
                                    </span>
                                )}
                                {!(futureStartDate || orientation.orientation_status === "Paused") && (
                                    <>
                                        <ProgressRating report={orientation.progress_report} outdatedWarning={true} />
                                        <span className="d-flex align-items-center gap-3 ms-sm-2 fs-7">
                                            <span className="text-dark fw-bold fs-7 me-1">Reports:</span>
                                            <SubmissionStatus
                                                label="OSR"
                                                isSubmitted={orientation.osr_submission_status}
                                            />
                                            <SubmissionStatus
                                                label="NHSR"
                                                isSubmitted={orientation.nhsr_submission_status}
                                            />
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card-toolbar gap-2 gap-sm-2 w-100 w-sm-auto">
                    <button
                        className="btn btn-light-primary flex-center justify-content-center flex-fill flex-sm-grow-0 p-3"
                        onClick={() => navigate(`/educator-orientations/${orientation.id}`)}
                    >
                        <i className="fas fa-arrow-right fs-4"></i>
                        <span className="fw-bold ps-2">Progress Details</span>
                    </button>
                    <Dropdown role="menuitem">
                        <Dropdown.Toggle
                            aria-controls={"orientee_actions"}
                            id="dropdown_actions"
                            className="btn-light-primary p-3 no-caret"
                        >
                            <i className="fas fa-ellipsis fs-4 pe-0"></i>
                            {progressReportDue() && (
                                <span className="position-absolute top-0 start-100 translate-middle ">
                                    <i className="fas fa-circle text-danger"></i>
                                </span>
                            )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu as={"ul"} className="p-4 w-250px border-1" id="orientee_actions" role={"menu"}>
                            {progressReportDue() && (
                                <>
                                    <Dropdown.Header as={"div"} className="fs-5 fw-bold px-3 py-0 text-dark">
                                        Due
                                    </Dropdown.Header>
                                    {missingWeeks.map((missingWeek) => (
                                        <Dropdown.Item
                                            key={missingWeek}
                                            as={"li"}
                                            className="menu-item py-0"
                                            role="menuitem"
                                        >
                                            <button
                                                className="btn btn-link menu-link w-100 pr-0"
                                                onClick={() => {
                                                    navigate(
                                                        `/educator-orientations/${orientation.id}?week=${missingWeek}`
                                                    )
                                                }}
                                            >
                                                <span className="menu-title fw-bold px-3">
                                                    Week {missingWeek} Progress Report
                                                </span>
                                            </button>
                                        </Dropdown.Item>
                                    ))}
                                </>
                            )}
                            <div className="separator separator-dotted my-2"></div>
                            <Dropdown.Header as={"div"} className="fs-5 fw-bold px-3 py-0 text-dark">
                                Manage
                            </Dropdown.Header>
                            <Dropdown.Item as={"li"} className="menu-item py-0" role="menuitem">
                                <button
                                    className="btn btn-link menu-link w-100"
                                    onClick={() => {
                                        navigate(`/educator-orientations/${orientation.id}/preceptors`)
                                    }}
                                >
                                    <span className="menu-title fw-bold px-3">Assigned Preceptors</span>
                                </button>
                            </Dropdown.Item>
                            <Dropdown.Item as={"li"} className="menu-item py-0" role="menuitem">
                                <button
                                    className="btn btn-link menu-link w-100"
                                    onClick={() => {
                                        navigate(`/educator-orientations/${orientation.id}/status`)
                                    }}
                                >
                                    <span className="menu-title fw-bold px-3">Orientee Status</span>
                                </button>
                            </Dropdown.Item>
                            <div className="separator separator-dotted my-2"></div>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
            <div className="card-body py-4 d-none d-sm-block">
                <div className="row g-4">
                    <div className="col">
                        <EducatorOrientationFields orientation={orientation} />
                    </div>
                </div>
                {orientation.current_readiness_rating > 0 && (
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
                        <div className="col">
                            <div className="d-flex fw-bold justify-content-between align-items-center gap-2 flex-fill border rounded p-3">
                                Clinical Practice:
                                <span
                                    className={`badge ${clinicalBadge.background} ${clinicalBadge.textColor} flex-center min-w-45px px-3 py-2`}
                                >
                                    {clinicalBadge.score}%
                                </span>
                            </div>
                        </div>
                        <div className="col">
                            <div className="d-flex fw-bold justify-content-between align-items-center gap-2 flex-fill border rounded p-3">
                                Medication Management:
                                <span
                                    className={`badge ${medicationBadge.background} ${medicationBadge.textColor} flex-center min-w-45px px-3 py-2`}
                                >
                                    {medicationBadge.score}%
                                </span>
                            </div>
                        </div>
                        <div className="col">
                            <div className="d-flex fw-bold justify-content-between align-items-center gap-2 flex-fill border rounded p-3">
                                Assessment & Care:
                                <span
                                    className={`badge ${assessmentBadge.background} ${assessmentBadge.textColor} flex-center min-w-45px px-3 py-2`}
                                >
                                    {assessmentBadge.score}%
                                </span>
                            </div>
                        </div>
                        <div className="col">
                            <div className="d-flex fw-bold justify-content-between align-items-center gap-2 flex-fill border rounded p-3">
                                Prevention & Safety:
                                <span
                                    className={`badge ${preventionBadge.background} ${preventionBadge.textColor} flex-center min-w-45px px-3 py-2`}
                                >
                                    {preventionBadge.score}%
                                </span>
                            </div>
                        </div>
                        <div className="col">
                            <div className="d-flex fw-bold justify-content-between align-items-center gap-2 flex-fill border rounded p-3">
                                Education & Collaboration:
                                <span
                                    className={`badge ${educationBadge.background} ${educationBadge.textColor} flex-center min-w-45px px-3 py-2`}
                                >
                                    {educationBadge.score}%
                                </span>
                            </div>
                        </div>
                        <div className="col">
                            <div className="d-flex fw-bold justify-content-between align-items-center gap-2 flex-fill border rounded p-3">
                                Professionalism:
                                <span
                                    className={`badge ${professionalismBadge.background} ${professionalismBadge.textColor} flex-center min-w-45px px-3 py-2`}
                                >
                                    {professionalismBadge.score}%
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
